import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "wouter";
import { Menu, X, Calculator, ArrowRight } from "lucide-react";
import logoImage from "@assets/image_1757508460788.webp";
import LanguageSwitcher from "./language-switcher";
import { Button } from "@/components/ui/button";

export default function Navigation() {
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [, navigate] = useLocation();
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    // Always return focus to the trigger when closed via any path
    requestAnimationFrame(() => triggerRef.current?.focus());
  };

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll + trap focus when mobile drawer is open
  useEffect(() => {
    if (!isMobileMenuOpen) {
      document.body.style.overflow = "";
      return;
    }
    document.body.style.overflow = "hidden";

    const drawer = drawerRef.current;
    const focusables = drawer
      ? Array.from(
          drawer.querySelectorAll<HTMLElement>(
            'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((el) => !el.hasAttribute("disabled"))
      : [];
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    first?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setIsMobileMenuOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (e.key !== "Tab" || focusables.length === 0) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { href: "/#features", label: t("nav.features") },
    { href: "/#how-it-works", label: t("nav.howItWorks") },
    { href: "/compare", label: "Compare" },
    { href: "/guides", label: "Guides" },
  ];

  return (
    <>
      <a href="#main-content" className="skip-to-content">Skip to content</a>
      <nav
        className={`fixed left-0 right-0 z-50 transition-all duration-500 ease-emphasized ${
          isScrolled ? "top-0" : "top-10"
        }`}
        aria-label="Primary"
      >
        <div
          className={`mx-auto transition-all duration-500 ease-emphasized ${
            isScrolled ? "max-w-7xl px-3 sm:px-4 mt-2" : "max-w-7xl px-4 sm:px-6 mt-2"
          }`}
        >
          <div
            className={`glass rounded-2xl shadow-md transition-all duration-500 ease-emphasized ${
              isScrolled ? "py-2 px-3 sm:px-4" : "py-3 px-4 sm:px-6"
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              {/* Logo */}
              <Link href="/" className="flex items-center gap-2.5 group" data-testid="logo-realtyblocks">
                <div className="relative">
                  <span className="absolute inset-0 rounded-xl bg-gradient-brand opacity-20 blur-md group-hover:opacity-30 transition-opacity" />
                  <img
                    src={logoImage}
                    alt="PropGauge"
                    className={`relative object-contain transition-all duration-300 ${
                      isScrolled ? "w-7 h-7" : "w-9 h-9"
                    }`}
                  />
                </div>
                <div className="flex flex-col leading-tight">
                  <span className={`font-display font-bold tracking-tight gradient-text transition-all ${
                    isScrolled ? "text-base" : "text-lg"
                  }`}>
                    PropGauge™
                  </span>
                  <span className="text-[10px] sm:text-xs text-muted-foreground">
                    by RealtyBlocks
                  </span>
                </div>
              </Link>

              {/* Desktop nav */}
              <div className="hidden md:flex items-center gap-1">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground rounded-md hover:bg-muted/60 transition-colors"
                    data-testid={`link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {link.label}
                  </a>
                ))}
              </div>

              {/* Right cluster */}
              <div className="flex items-center gap-2">
                <div className="hidden sm:block">
                  <LanguageSwitcher compact={isScrolled} />
                </div>
                <Button
                  size={isScrolled ? "sm" : "default"}
                  variant="accent"
                  onClick={() => navigate("/#valuation")}
                  className="hidden sm:inline-flex"
                  data-testid="button-nav-cta"
                >
                  <Calculator className="h-4 w-4" />
                  Free valuation
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <button
                  ref={triggerRef}
                  className="md:hidden p-2 rounded-lg hover:bg-muted/60 active:bg-muted text-foreground min-w-[44px] min-h-[44px] flex items-center justify-center"
                  onClick={() => setIsMobileMenuOpen((v) => !v)}
                  aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                  aria-expanded={isMobileMenuOpen}
                  aria-controls="mobile-drawer"
                  data-testid="button-mobile-menu"
                >
                  {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 animate-fade-in"
          role="dialog"
          aria-modal="true"
          aria-label="Site menu"
          id="mobile-drawer"
        >
          <div
            className="absolute inset-0 bg-foreground/30 backdrop-blur-sm"
            onClick={closeMobileMenu}
            aria-hidden="true"
          />
          <div ref={drawerRef} className="absolute right-3 left-3 top-20 surface-elevated p-5 animate-slide-down">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={closeMobileMenu}
                  className="px-3 py-3 rounded-lg text-base font-medium text-foreground hover:bg-muted transition-colors"
                  data-testid={`mobile-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.label}
                </a>
              ))}
              <div className="border-t border-border my-2" />
              <div className="flex items-center justify-between px-3 py-2">
                <span className="text-sm text-muted-foreground">Language</span>
                <LanguageSwitcher compact />
              </div>
              <Button
                variant="accent"
                size="lg"
                className="mt-2"
                onClick={() => {
                  closeMobileMenu();
                  navigate("/#valuation");
                }}
              >
                <Calculator className="h-4 w-4" /> Get free valuation
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
